import { ref } from 'vue';
import { getPlanByVariant } from '~/constants/pricing';
import { stripeEmbedSrc } from '~/utils/checkoutEmbed';

// TypeScript interfaces pro LemonSqueezy data
interface CheckoutSessionData {
  variantId: string;
  /** Signed-in purchase; omitted for guest checkout, where `guestToken` stands in. */
  userId?: string;
  email?: string;
  redirectUrl?: string;
  discountCode?: string;
  /** Which plan the user picked; travels through LemonSqueezy custom data to the webhook. */
  planId?: string;
  /** The account-less CV this purchase is for: what was paid for it before comes off the pack. */
  generationId?: string;
  /**
   * Guest checkout for the single export: the parked CV this payment unlocks.
   * The buyer types their e-mail in LemonSqueezy and gets the link there.
   */
  guestToken?: string;
}

interface SubscriptionData {
  subscriptionId: string;
  planName: string;
  price: number;
  interval: string;
  intervalCount: number;
  status: string;
  statusFormatted: string;
  expiryDate: string | null;
}

interface CustomerData {
  name: string;
  email: string;
  status: string;
  statusFormatted: string;
  totalRevenue: number;
  totalRevenueFormatted: string;
  mrr: number;
  mrrFormatted: string;
}

/**
 * Checkouts for every plan. Despite the name, also the Czech and Polish
 * checkouts: a plan the visitor buys in crowns or złoty (usePricing) opens
 * Stripe's instead, and the
 * caller gets back a string its checkout frame shows either way
 * (utils/checkoutEmbed.ts).
 */
export const useLemonSqueezy = () => {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  const { getPlan } = usePremiumPlans();
  const user = useSupabaseUser();
  const { show } = useSnackbar();
  const { redirectToSignup } = useRequireAccount();
  const nuxtApp = useNuxtApp();
  const { trackBeginCheckout } = useAdsConversion();

  // Reactive state
  const isLoading = ref(false);
  const error = ref('');
  const isScriptLoading = ref(true);

  // Check if LemonSqueezy script is loading
  if (import.meta.client) {
    const checkScriptLoading = () => {
      const lemonLoading = nuxtApp.$lemonLoading?.() ?? true;
      isScriptLoading.value = lemonLoading;
    };

    // Initial check
    checkScriptLoading();

    // Wait for script to load
    nuxtApp.$lemonReady?.().then(() => {
      isScriptLoading.value = false;
    }).catch(() => {
      isScriptLoading.value = false;
    });

    // Periodic check as fallback
    const interval = setInterval(() => {
      checkScriptLoading();
      if (!isScriptLoading.value) {
        clearInterval(interval);
      }
    }, 100);

    // Cleanup after 10 seconds max
    setTimeout(() => {
      clearInterval(interval);
      if (isScriptLoading.value) {
        isScriptLoading.value = false;
      }
    }, 10000);
  } else {
    isScriptLoading.value = false;
  }

  const lemonAffiliateRef = (): string | undefined => {
    if (!import.meta.client) return undefined;
    try {
      const id = window.LemonSqueezy?.Affiliate?.GetID?.();
      return id ? String(id) : undefined;
    } catch {
      return undefined;
    }
  };

  /** The plan as this visitor buys it, by id or by its LemonSqueezy variant. */
  const planFor = (data: Pick<CheckoutSessionData, 'planId' | 'variantId'>) => {
    const id = data.planId ?? getPlanByVariant(config.public, data.variantId)?.id;
    return id ? getPlan(id as Parameters<typeof getPlan>[0]) : undefined;
  };

  /**
   * The Czech and Polish checkouts: Stripe, embedded in the page like LemonSqueezy's
   * iframe, or as Stripe's own page when the caller leaves the site (`hosted`).
   */
  const createStripeCheckout = async (data: CheckoutSessionData, planId: string, currency: string, hosted = false): Promise<string | null> => {
    const response = await $fetch<{ clientSecret?: string; url?: string; error?: string }>(
      '/api/stripe/create-checkout-session',
      {
        method: 'POST',
        // `tiktok` only with cookie consent, for the server's TikTok events.
        body: { ...data, planId, currency, hosted, cancelUrl: hosted ? window.location.href : undefined, tiktok: tiktokCheckoutContext() },
      }
    );
    if (response.error) {
      error.value = response.error;
      show(t('premium.checkoutError'));
      return null;
    }
    const src = hosted ? response.url : response.clientSecret && stripeEmbedSrc(response.clientSecret);
    if (src) trackBeginCheckout(planId, data.variantId);
    return src || null;
  };

  // Checkout session creation
  const createCheckoutSession = async (data: CheckoutSessionData, options: { hosted?: boolean } = {}): Promise<string | null> => {
    try {
      isLoading.value = true;
      error.value = '';

      const plan = planFor(data);
      if (plan?.provider === 'stripe') return await createStripeCheckout(data, plan.id, plan.currency, options.hosted);

      // Wait for LemonSqueezy script to load
      if (import.meta.client && nuxtApp.$lemonReady) {
        await nuxtApp.$lemonReady();
      }

      const response = await $fetch<{ url?: string; error?: string }>(
        '/api/lemonsqueezy/create-checkout-session',
        {
          method: 'POST',
          // A LemonSqueezy affiliate who referred this visitor: the server
          // decides between them and ours (last click) and adds ?aff_ref.
          // `tiktok` only with cookie consent, for the server's TikTok events.
          body: { ...data, lemonAffiliateRef: lemonAffiliateRef(), tiktok: tiktokCheckoutContext() },
        }
      );

      if (response.error) {
        error.value = response.error;
        show(t('premium.checkoutError'));
        return null;
      }

      // Every checkout passes here, account or guest, iframe or redirect.
      if (response.url) trackBeginCheckout(data.planId, data.variantId);
      return response.url || null;
    } catch {
      const errorMessage = t('premium.checkoutError');
      error.value = errorMessage;
      show(errorMessage);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  // Check subscription status
  const checkSubscription = async (): Promise<boolean> => {
    if (!user.value) return false;

    try {
      isLoading.value = true;
      error.value = '';

      const response = await $fetch<{ isPremium: boolean; error?: string }>(
        '/api/lemonsqueezy/check-subscription'
      );

      if (response.error) {
        error.value = response.error;
        return false;
      }

      return response.isPremium;
    } catch {
      error.value = t('premium.checkError');
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Load subscription details
  const loadSubscriptionDetails = async (): Promise<SubscriptionData[]> => {
    if (!user.value) return [];

    try {
      isLoading.value = true;
      error.value = '';

      const response = await $fetch<{ subscriptions: SubscriptionData[]; error?: string }>(
        `/api/lemonsqueezy/subscription-details?userId=${user.value.id}`
      );

      if (response.error) {
        error.value = response.error;
        return [];
      }

      return response.subscriptions || [];
    } catch {
      error.value = t('premium.subscriptionDetail.loading');
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  // Load customer details
  const loadCustomerDetails = async (): Promise<CustomerData | null> => {
    if (!user.value) return null;

    try {
      isLoading.value = true;
      error.value = '';

      const response = await $fetch<CustomerData & { error?: string }>(
        `/api/lemonsqueezy/customer-details?userId=${user.value.id}`
      );

      if (response.error) {
        error.value = response.error;
        return null;
      }

      return response;
    } catch {
      error.value = t('premium.customerDetail.loading');
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  // Redirect to checkout
  const redirectToCheckout = async (variantId: string, redirectUrl?: string, discountCode?: string) => {
    // Signed out: send them to sign-up, where the reason is spelled out.
    if (!user.value) {
      await redirectToSignup();
      return;
    }

    const checkoutUrl = await createCheckoutSession({
      variantId,
      userId: user.value.id,
      email: user.value.email || '',
      redirectUrl,
      discountCode,
    }, { hosted: true });

    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  return {
    // State
    isLoading: readonly(isLoading),
    isScriptLoading: readonly(isScriptLoading),
    error: readonly(error),

    // Methods
    createCheckoutSession,
    checkSubscription,
    loadSubscriptionDetails,
    loadCustomerDetails,
    redirectToCheckout,
  };
};
